/**
 * Protected paths that require authentication.
 * If auth/me returns 401 and user is on one of these paths → redirect to login.
 * All other pages remain accessible without login.
 */
const PROTECTED_PATHS = [
    '/profile',
    '/notifications',
    '/settings',
    '/checkout',
    '/recommended-jobs',
    '/saved-jobs',
    '/upcoming-interviews',
    '/application-timeline',
    '/test-instruction',
    '/test-app',
];

/**
 * Check if user is logged in. If localStorage has user return true.
 * @returns {Promise<boolean>}
 */
export const isUserLoggedIn = async () => {
    if (localStorage.getItem('user')) return true;
    try {
        const res = await fetch('/api/auth/me?app=job', { credentials: 'include' });
        if (res.status === 401) {
            localStorage.removeItem('user');
            const path = window.location.pathname || '';
            // Only redirect on protected pages
            const isProtected = PROTECTED_PATHS.some(p => path === p || path.startsWith(p + '/'));
            if (isProtected) {
                const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
                window.location.href = `/login?returnUrl=${returnUrl}`;
            }
            return false;
        }
        if (res.ok) {
            const data = await res.json();
            if (data.success && data.user) {
                localStorage.setItem('user', JSON.stringify(data.user));
                return true;
            }
        }
    } catch (e) {
        // Ignore - user not logged in
    }
    return false;
};

/**
 * Sync auth check - returns cached result from localStorage.
 * @returns {boolean}
 */
export const isUserLoggedInSync = () => !!localStorage.getItem('user');

/** @returns {Promise<boolean>} */
export const verifyAuth = () => isUserLoggedIn();

/**
 * Get logged in user data from localStorage
 */
export const getLoggedInUser = () => {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    try {
        return JSON.parse(userStr);
    } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
    }
};

/**
 * Set user as logged in (store in localStorage)
 */
export const loginUser = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
};

/**
 * Log out - delete cookie via backend, clear localStorage, reload
 */
export const logoutUser = async () => {
    try {
        await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (error) {
        console.error('Logout API call failed:', error);
    }
    localStorage.removeItem('user');
    window.location.href = '/';
};

/**
 * Update user data in localStorage
 */
export const updateUserData = (updates) => {
    const currentUser = getLoggedInUser();
    if (currentUser) {
        const updatedUser = { ...currentUser, ...updates };
        localStorage.setItem('user', JSON.stringify(updatedUser));
    }
};

/**
 * Get user's display name
 */
export const getUserName = () => {
    const user = getLoggedInUser();
    return user?.name || user?.first_name || 'Guest';
};

/**
 * Get user's avatar/image URL
 */
export const getUserAvatar = () => {
    const user = getLoggedInUser();
    return user?.image || `https://api.dicebear.com/7.x/avataaars/svg?seed=${getUserName()}`;
};

/**
 * Get user's role
 */
export const getUserRole = () => {
    const user = getLoggedInUser();
    return user?.role || 'User';
};

/**
 * Login via API - backend sets HttpOnly cookie
 */
const login = async (credentials) => {
    const { username, email, password, rememberMe } = credentials;
    const payload = { password, rememberMe, app: 'job' };
    if (email) payload.email = email;
    if (username) payload.username = username;

    const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();
    if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
};

/**
 * Get auth state
 */
const getAuthState = () => {
    const user = getLoggedInUser();
    if (!user) return { loggedIn: false };
    return {
        loggedIn: true,
        userId: user.id || null,
        email: user.email || null,
        username: user.username || user.email || null,
    };
};

/**
 * Refresh access token
 */
const refreshToken = async () => {
    const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
    });
    if (!response.ok) {
        await logoutUser();
        throw new Error('Token refresh failed');
    }
    return true;
};

/** Default export for Login/ProfilePage that use authService.login(), authService.updateUser(), etc. */
const authService = {
    login,
    logout: logoutUser,
    getUser: getLoggedInUser,
    isLoggedIn: isUserLoggedIn,
    isAuthenticated: isUserLoggedInSync,
    getAuthState,
    refreshToken,
    updateUser: updateUserData,
};

export default authService;
